<template>
  <a-modal
    :visible="visible"
    :width="1000"
    :footer="null"
    :mask-closable="false"
    wrapClassName="modalWidth2 tech-export-modal-wrap"
    dialogClass="modalWidth2"
    @cancel="close"
  >
    <template slot="title">
      <div class="modal-header-title">
        <a-icon type="cloud-download" class="title-icon" />
        <span class="main-title">数据导出</span>
        <span class="site-tag" v-if="info.siteName">{{ info.siteName }}</span>
        <span class="type-tag">{{ typeName }}</span>
      </div>
    </template>

    <div class="export-modal-body">
      <a-tabs v-model="activeTab" class="tech-tabs" @change="onTabChange">
        <!-- 标签页 1: 导出任务 -->
        <a-tab-pane key="task" tab="导出任务">
          <div class="tab-task-container">
            <!-- 站点与监测类型概要卡片 -->
            <div class="site-info-box">
              <div class="info-item">
                <span class="item-label">当前站点：</span>
                <span class="item-value site-highlight">{{ info.siteName || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="item-label">监测类型：</span>
                <span class="item-value">{{ typeName }}</span>
              </div>
              <div class="info-item" v-if="factorCount">
                <span class="item-label">导出因子：</span>
                <span class="item-value">{{ factorCount }} 个（与当前勾选一致）</span>
              </div>
            </div>

            <!-- 时间选择区 -->
            <div class="form-section">
              <div class="form-row">
                <span class="form-label"><span class="required">*</span> 导出时间范围：</span>
                <a-range-picker
                  v-model="range"
                  class="tech-range-picker"
                  style="width: 380px"
                  :allow-clear="true"
                  show-time
                  format="YYYY-MM-DD HH:mm:ss"
                  :placeholder="['导出开始时间', '导出结束时间']"
                  :disabled-date="disabledDate"
                  @calendarChange="onCalendarChange"
                  @openChange="onOpenChange"
                  @change="onDateChange"
                  @ok="onDateOk"
                />
              </div>

              <div class="tip-box">
                <p><a-icon type="info-circle" style="color: #1890ff; margin-right: 4px" /> 导出时间独立于页面查询条件，修改导出时间不会影响当前页面的查询与分页。</p>
                <p><a-icon type="clock-circle" style="color: #faad14; margin-right: 4px" /> 单次导出最长 31 天数据，后台异步流式生成，提交后自动切换至【导出文件】查看进度并下载。</p>
              </div>
            </div>

            <!-- 底部提交操作区 -->
            <div class="task-footer">
              <a-button class="btn-cancel" @click="close">取 消</a-button>
              <a-button type="primary" :loading="submitting" class="btn-submit" @click="submit">
                <a-icon type="cloud-upload" /> 提 交 导 出
              </a-button>
            </div>
          </div>
        </a-tab-pane>

        <!-- 标签页 2: 导出文件 (仅展示当前站点与当前数据类型的历史记录) -->
        <a-tab-pane key="files" tab="导出文件">
          <div class="tab-files-container">
            <!-- 顶部过滤与操作条 (无多余搜索下拉框) -->
            <div class="files-header">
              <div class="scope-tip">
                <a-icon type="file-search" style="color: #00e5ff; margin-right: 4px" />
                当前仅展示 <span class="site-highlight">{{ info.siteName || '当前站点' }}</span> 的 <span class="type-highlight">{{ typeName }}</span> 导出文件
              </div>
              <div class="header-actions">
                <a-button size="small" icon="reload" class="btn-refresh" :loading="loading" @click="reload">刷新列表</a-button>
                <span class="retention-text">
                  <a-icon type="safety-certificate" theme="twoTone" two-tone-color="#52c41a" style="margin-right: 3px" />
                  文件保留 30 天
                </span>
              </div>
            </div>

            <!-- 文件数据表格 (自适应无滚动条, 文件名换行, 全部列完整显示) -->
            <a-table
              class="tech-file-table"
              size="middle"
              row-key="id"
              :columns="columns"
              :data-source="dataSource"
              :pagination="ipagination"
              :loading="loading"
              @change="handleTableChange"
            >
              <!-- 文件名 (自然换行完整显示) -->
              <template slot="fileNameSlot" slot-scope="text, record">
                <div class="file-info-box">
                  <a-icon type="file-excel" theme="twoTone" two-tone-color="#52c41a" class="excel-icon" />
                  <span class="file-name-text">
                    {{ record.fileName || record.taskName || '-' }}
                  </span>
                </div>
              </template>

              <!-- 导出时间范围 -->
              <template slot="rangeSlot" slot-scope="text, record">
                <div class="range-time-cell">
                  <div>{{ formatTime(record.beginTime) }}</div>
                  <div class="time-split">至</div>
                  <div>{{ formatTime(record.endTime) }}</div>
                </div>
              </template>

              <!-- 数据行数 -->
              <template slot="recordCountSlot" slot-scope="text">
                <span class="row-count">{{ text == null ? '-' : Number(text).toLocaleString() }} 行</span>
              </template>

              <!-- 状态 (单行胶囊徽章) -->
              <template slot="statusSlot" slot-scope="text, record">
                <a-tooltip :title="text == 3 ? (record.failReason || '导出失败') : ''">
                  <div class="status-badge" :class="'status-' + text">
                    <a-icon v-if="text == 1" type="loading" style="margin-right: 4px; flex-shrink: 0" />
                    <span v-else class="status-dot"></span>
                    <span class="status-text">{{ statusName(text) }}</span>
                  </div>
                </a-tooltip>
              </template>

              <!-- 操作 (生成中支持停止, 排队中支持取消, 完成/失败支持删除) -->
              <template slot="actionSlot" slot-scope="text, record">
                <div class="table-actions">
                  <!-- 生成中: 停止按钮 -->
                  <a-popconfirm
                    v-if="record.status == 1"
                    title="确定终止正在生成的导出任务吗？"
                    ok-text="终止"
                    cancel-text="继续生成"
                    @confirm="cancelTask(record)"
                  >
                    <span class="btn-stop" title="点击终止后台生成">
                      <a-icon type="pause-circle" />停止
                    </span>
                  </a-popconfirm>

                  <!-- 排队中: 取消按钮 -->
                  <a-popconfirm
                    v-else-if="record.status == 0"
                    title="确定取消该排队中的导出任务吗？"
                    ok-text="取消任务"
                    cancel-text="不取消"
                    @confirm="cancelTask(record)"
                  >
                    <span class="btn-cancel" title="点击取消任务排队">
                      <a-icon type="close-circle" />取消
                    </span>
                  </a-popconfirm>

                  <!-- 成功完成: 下载 + 删除 -->
                  <template v-else-if="record.status == 2">
                    <a-button
                      v-if="record.fileId"
                      type="primary"
                      size="small"
                      shape="round"
                      icon="download"
                      class="btn-dl"
                      @click="download(record)"
                    >
                      下载
                    </a-button>
                    <span v-else class="btn-dl-disabled" title="文件不存在">
                      <a-icon type="download" style="margin-right: 4px" />下载
                    </span>

                    <a-popconfirm
                      title="确定删除该导出记录及对应文件吗？"
                      ok-text="删除"
                      cancel-text="取消"
                      @confirm="remove(record)"
                    >
                      <span class="btn-del">
                        <a-icon type="delete" />删除
                      </span>
                    </a-popconfirm>
                  </template>

                  <!-- 失败或已取消: 禁用下载 + 删除 -->
                  <template v-else>
                    <a-tooltip :title="record.failReason || '生成失败'">
                      <span class="btn-dl-disabled">
                        <a-icon type="download" style="margin-right: 4px" />下载
                      </span>
                    </a-tooltip>

                    <a-popconfirm
                      title="确定删除该导出记录吗？"
                      ok-text="删除"
                      cancel-text="取消"
                      @confirm="remove(record)"
                    >
                      <span class="btn-del">
                        <a-icon type="delete" />删除
                      </span>
                    </a-popconfirm>
                  </template>
                </div>
              </template>

              <template slot="emptySlot">
                <div class="empty-holder">
                  <a-icon type="file-excel" class="empty-icon" />
                  <p>当前站点暂无 {{ typeName }} 导出记录</p>
                </div>
              </template>
            </a-table>
          </div>
        </a-tab-pane>
      </a-tabs>
    </div>
  </a-modal>
</template>

<script>
import moment from 'moment'
import { createExportTask, listExportTasks, cancelExportTask, deleteExportTask } from '@/api/exportCenter'

const MAX_EXPORT_DAYS = 31

const STATUS_QUEUED = 0
const STATUS_GENERATING = 1
const STATUS_SUCCESS = 2
const STATUS_FAILED = 3

export default {
  name: 'CreateExportDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    info: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      moment,
      activeTab: 'task',
      range: [],
      submitting: false,
      firstDate: null,
      loading: false,
      dataSource: [],
      listTimer: null,
      ipagination: {
        current: 1,
        pageSize: 8,
        pageSizeOptions: ['8', '16', '32'],
        showTotal: (total, range) => `${range[0]}-${range[1]} 共 ${total} 条`,
        showQuickJumper: true,
        showSizeChanger: true,
        total: 0
      },
      // 优化列宽：无横向滚动条，文件名换行自适应撑满，其余列紧凑对齐
      columns: [
        { title: '文件名', dataIndex: 'fileName', key: 'fileName', scopedSlots: { customRender: 'fileNameSlot' } },
        { title: '导出时间范围', dataIndex: 'beginTime', key: 'beginTime', width: 145, align: 'center', scopedSlots: { customRender: 'rangeSlot' } },
        { title: '数据行数', dataIndex: 'recordCount', key: 'recordCount', width: 75, align: 'center', scopedSlots: { customRender: 'recordCountSlot' } },
        { title: '生成时间', dataIndex: 'createTime', key: 'createTime', width: 135, align: 'center' },
        { title: '状态', dataIndex: 'status', key: 'status', width: 85, align: 'center', scopedSlots: { customRender: 'statusSlot' } },
        { title: '操作', key: 'action', width: 135, align: 'center', scopedSlots: { customRender: 'actionSlot' } }
      ]
    }
  },
  computed: {
    typeName() {
      return { '2011': '实时数据', '2051': '分钟数据', '2061': '小时数据' }[this.info.dataType] || '监测数据'
    },
    factorCount() {
      if (!this.info.factorCodes) return 0
      return this.info.factorCodes.split(',').filter(code => !!code).length
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.activeTab = (this.info && this.info.defaultTab) || 'task'
        this.initRange()
        this.loadList()
      } else {
        this.stopListTimer()
      }
    },
    activeTab(val) {
      if (val === 'files') {
        this.loadList()
        this.startListTimer()
      } else {
        this.stopListTimer()
      }
    }
  },
  beforeDestroy() {
    this.stopListTimer()
  },
  methods: {
    initRange() {
      this.firstDate = null
      const suggested = this.info.suggestedRange
      if (suggested && suggested[0] && suggested[1]) {
        this.range = [moment(suggested[0], 'YYYY-MM-DD HH:mm:ss'), moment(suggested[1], 'YYYY-MM-DD HH:mm:ss')]
      } else {
        this.range = [moment().startOf('day'), moment().endOf('day')]
      }
    },
    // 时间跨度控制：支持任意历史年份/月份，单次跨度最大 31 天，不能选未来时间
    disabledDate(current) {
      if (!current) return false
      // 不能选未来日期
      if (current > moment().endOf('day')) return true
      // 如果正在选择第 2 个日期，限制与第 1 个日期的最大跨度为 31 天（前后均可）
      if (this.firstDate) {
        const minDate = this.firstDate.clone().subtract(MAX_EXPORT_DAYS, 'days').startOf('day')
        const maxDate = this.firstDate.clone().add(MAX_EXPORT_DAYS, 'days').endOf('day')
        return current < minDate || current > maxDate || current > moment().endOf('day')
      }
      // 未选第 1 个日期时，所有历史日期均可自由选择
      return false
    },
    onCalendarChange(dates) {
      if (dates && dates.length > 0 && dates[0]) {
        this.firstDate = moment(dates[0])
      } else {
        this.firstDate = null
      }
    },
    onOpenChange(open) {
      if (!open) {
        this.firstDate = null
      }
    },
    onDateChange(dates) {
      this.firstDate = null
      if (!dates || dates.length === 0) {
        this.range = []
      }
    },
    onDateOk() {
      this.firstDate = null
    },
    onTabChange(key) {
      if (key === 'files') {
        this.reload()
      }
    },
    close() {
      this.$emit('update:visible', false)
      this.firstDate = null
      this.stopListTimer()
    },
    statusName(status) {
      if (status == STATUS_QUEUED) return '排队中'
      if (status == STATUS_GENERATING) return '生成中'
      if (status == STATUS_SUCCESS) return '已完成'
      if (status == STATUS_FAILED) return '失败'
      return '未知'
    },
    formatTime(time) {
      return time ? moment(time).format('YYYY-MM-DD HH:mm') : '-'
    },
    handleTableChange(pagination) {
      this.ipagination.current = pagination.current
      this.ipagination.pageSize = pagination.pageSize
      this.loadList()
    },
    reload() {
      this.ipagination.current = 1
      this.loadList()
    },
    loadList() {
      if (!this.visible) return
      this.loading = true
      // 仅查询当前站点(scopeId)和当前数据类型(dataType)
      const params = {
        current: this.ipagination.current,
        size: this.ipagination.pageSize,
        dataType: this.info.dataType || undefined,
        scopeId: this.info.scopeId || undefined
      }
      listExportTasks(params).then(res => {
        if (res.success) {
          const page = res.result || {}
          this.dataSource = page.records || []
          this.ipagination.total = page.total || 0
          const hasActive = this.dataSource.some(item => item.status == STATUS_QUEUED || item.status == STATUS_GENERATING)
          if (hasActive && this.activeTab === 'files') {
            this.startListTimer()
          } else if (!hasActive) {
            this.stopListTimer()
          }
        }
      }).catch(() => {}).finally(() => {
        this.loading = false
      })
    },
    startListTimer() {
      if (this.listTimer) return
      this.listTimer = setInterval(() => {
        if (document.hidden || !this.visible || this.activeTab !== 'files') return
        this.loadList()
      }, 3500)
    },
    stopListTimer() {
      if (this.listTimer) {
        clearInterval(this.listTimer)
        this.listTimer = null
      }
    },
    submit() {
      if (this.submitting) return
      if ((this.info.scope || 0) != 1 || !this.info.scopeId) {
        this.$message_custom('仅支持单站点导出', 'warning')
        return
      }
      if (!this.range || this.range.length !== 2 || !this.range[0] || !this.range[1]) {
        this.$message_custom('请选择导出时间范围', 'warning')
        return
      }
      const stime = this.range[0].format('YYYY-MM-DD HH:mm:ss')
      const etime = this.range[1].format('YYYY-MM-DD HH:mm:ss')
      if (moment(stime).isAfter(moment(etime))) {
        this.$message_custom('开始时间不能大于结束时间', 'warning')
        return
      }
      if (moment(etime).diff(moment(stime), 'days', true) > MAX_EXPORT_DAYS) {
        this.$message_custom('单次导出时间最大跨度不能超过 31 天', 'warning')
        return
      }
      const params = {
        dataType: this.info.dataType,
        scope: this.info.scope,
        scopeId: this.info.scopeId,
        st: this.info.st,
        stime: stime,
        etime: etime,
        factorCodes: this.info.factorCodes,
        onlineData: this.info.onlineData,
        productionStatus: this.info.productionStatus,
        column: 'data_time',
        order: 'desc'
      }
      this.submitting = true
      createExportTask(params).then(res => {
        if (res.success) {
          this.$message_custom('导出任务已提交，正在生成...', 'success', 3)
          // 提交后自动切换到【导出文件】标签页，实时看到当前任务
          this.activeTab = 'files'
          this.reload()
          this.startListTimer()
        } else {
          this.$message_custom(res.message || '提交失败', 'error', 5)
        }
      }).catch(() => {
        this.$message_custom('提交失败，请稍后重试', 'error', 5)
      }).finally(() => {
        this.submitting = false
      })
    },
    download(record) {
      window.open(window._CONFIG['domianURL'] + '/sys/common/file/download/' + record.fileId)
    },
    cancelTask(record) {
      cancelExportTask(record.id).then(res => {
        if (res.success) {
          this.$message_custom('任务已取消/终止', 'success')
          this.loadList()
        } else {
          this.$message_custom(res.message || '操作失败', 'error')
        }
      }).catch(() => {
        this.$message_custom('操作失败，请稍后重试', 'error')
      })
    },
    remove(record) {
      deleteExportTask(record.id).then(res => {
        if (res.success) {
          this.$message_custom('删除成功', 'success')
          this.loadList()
        } else {
          this.$message_custom(res.message || '删除失败', 'error')
        }
      }).catch(() => {
        this.$message_custom('删除失败', 'error')
      })
    }
  }
}
</script>

<style lang="less" scoped>
/* 弹窗头部 */
.modal-header-title {
  display: flex;
  align-items: center;
  .title-icon {
    font-size: 18px;
    color: #00e5ff;
    margin-right: 8px;
  }
  .main-title {
    font-size: 16px;
    font-weight: 700;
    color: #ffffff;
  }
  .site-tag {
    background: rgba(24, 144, 255, 0.25);
    border: 1px solid rgba(24, 144, 255, 0.6);
    color: #40a9ff;
    font-size: 12px;
    padding: 1px 8px;
    border-radius: 4px;
    margin-left: 10px;
    font-weight: normal;
  }
  .type-tag {
    background: rgba(19, 194, 194, 0.25);
    border: 1px solid rgba(19, 194, 194, 0.6);
    color: #36cfc9;
    font-size: 12px;
    padding: 1px 8px;
    border-radius: 4px;
    margin-left: 6px;
    font-weight: normal;
  }
}

.export-modal-body {
  padding: 4px 2px;
}

/* Tab 样式定制 */
.tech-tabs {
  /deep/ .ant-tabs-bar {
    border-bottom: 1px solid rgba(24, 144, 255, 0.35);
    margin-bottom: 16px;
  }
  /deep/ .ant-tabs-nav .ant-tabs-tab {
    color: rgba(255, 255, 255, 0.7);
    font-size: 15px;
    font-weight: 600;
    padding: 10px 22px;
    &:hover {
      color: #00e5ff;
    }
  }
  /deep/ .ant-tabs-nav .ant-tabs-tab-active {
    color: #00e5ff !important;
    text-shadow: 0 0 8px rgba(0, 229, 255, 0.6);
  }
  /deep/ .ant-tabs-ink-bar {
    background-color: #00e5ff;
    box-shadow: 0 0 8px #00e5ff;
    height: 3px;
  }
}

/* Tab 1: 创建任务 */
.tab-task-container {
  padding: 6px 4px;
}

.site-info-box {
  display: flex;
  flex-wrap: wrap;
  padding: 12px 16px;
  background: rgba(10, 36, 80, 0.85);
  border: 1px solid rgba(24, 144, 255, 0.35);
  border-radius: 6px;
  margin-bottom: 18px;

  .info-item {
    margin-right: 28px;
    line-height: 28px;
    font-size: 14px;
    .item-label {
      color: rgba(255, 255, 255, 0.65);
    }
    .item-value {
      color: #ffffff;
      font-weight: 600;
    }
    .site-highlight {
      color: #00e5ff;
    }
  }
}

.form-section {
  .form-row {
    display: flex;
    align-items: center;
    margin-bottom: 14px;
    .form-label {
      width: 120px;
      font-size: 14px;
      font-weight: 600;
      color: #ffffff;
      .required {
        color: #ff4d4f;
        margin-right: 2px;
      }
    }
  }
}

.tip-box {
  margin-top: 14px;
  padding: 12px 14px;
  background: rgba(8, 28, 64, 0.65);
  border: 1px solid rgba(24, 144, 255, 0.25);
  border-radius: 6px;
  p {
    margin: 4px 0;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.75);
    line-height: 1.5;
  }
}

.task-footer {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  .btn-cancel {
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.25);
    color: rgba(255, 255, 255, 0.85);
    margin-right: 12px;
    padding: 0 18px;
    height: 36px;
    border-radius: 4px;
    &:hover {
      border-color: #ffffff;
      color: #ffffff;
    }
  }

  .btn-submit {
    height: 36px;
    padding: 0 24px;
    font-size: 14px;
    font-weight: 600;
    background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
    border: none;
    box-shadow: 0 2px 10px rgba(24, 144, 255, 0.45);
    border-radius: 4px;
    &:hover {
      box-shadow: 0 4px 14px rgba(0, 229, 255, 0.6);
    }
  }
}

/* Tab 2: 导出文件列表 */
.tab-files-container {
  padding: 2px 0;
}

.files-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: rgba(8, 28, 64, 0.7);
  border: 1px solid rgba(24, 144, 255, 0.25);
  border-radius: 4px;

  .scope-tip {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.85);
    .site-highlight {
      color: #00e5ff;
      font-weight: 600;
    }
    .type-highlight {
      color: #36cfc9;
      font-weight: 600;
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    .btn-refresh {
      background: rgba(10, 36, 80, 0.85);
      border: 1px solid rgba(24, 144, 255, 0.4);
      color: #ffffff;
      margin-right: 12px;
      &:hover {
        border-color: #00e5ff;
        color: #00e5ff;
      }
    }
    .retention-text {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.55);
    }
  }
}

/* 表格深度定制 */
.tech-file-table {
  /deep/ .ant-table {
    background: transparent !important;
    color: #ffffff;
    width: 100%;
  }

  /deep/ .ant-table-body {
    overflow-x: hidden !important;
  }

  /deep/ .ant-table-thead > tr > th {
    background: #0a3478 !important;
    color: #00e5ff !important;
    font-weight: 700;
    font-size: 13px;
    border-bottom: 1px solid rgba(0, 229, 255, 0.4) !important;
    border-top: none !important;
    border-left: none !important;
    border-right: none !important;
    padding: 8px 4px;
    box-sizing: border-box;
  }

  /deep/ .ant-table-tbody > tr > td {
    background: rgba(8, 28, 66, 0.65) !important;
    color: #ffffff;
    font-size: 13px;
    border-bottom: 1px solid rgba(24, 144, 255, 0.18) !important;
    border-top: none !important;
    border-left: none !important;
    border-right: none !important;
    padding: 8px 4px;
    box-sizing: border-box;
  }

  /deep/ .ant-table-tbody > tr:hover > td {
    background: rgba(24, 144, 255, 0.2) !important;
  }
}

.file-info-box {
  display: flex;
  align-items: flex-start;
  text-align: left;
  word-break: break-all;
  .excel-icon {
    font-size: 16px;
    margin-right: 6px;
    margin-top: 2px;
    flex-shrink: 0;
  }
  .file-name-text {
    font-weight: 600;
    color: #ffffff;
    font-size: 13px;
    line-height: 1.45;
    word-break: break-all;
    white-space: normal;
  }
}

.range-time-cell {
  font-size: 12px;
  line-height: 1.3;
  color: #e6f7ff;
  .time-split {
    color: rgba(255, 255, 255, 0.35);
    font-size: 10px;
  }
}

.row-count {
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  color: #00e5ff;
  font-weight: 600;
}

/* 状态徽章 (单行不折行) */
.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  flex-shrink: 0;

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 5px;
    flex-shrink: 0;
  }

  .status-text {
    white-space: nowrap;
  }

  &.status-0 {
    background: rgba(250, 173, 20, 0.15);
    border: 1px solid rgba(250, 173, 20, 0.5);
    color: #ffc53d;
    .status-dot { background: #ffc53d; }
  }

  &.status-1 {
    background: rgba(24, 144, 255, 0.2);
    border: 1px solid rgba(24, 144, 255, 0.6);
    color: #40a9ff;
  }

  &.status-2 {
    background: rgba(82, 196, 26, 0.2);
    border: 1px solid rgba(82, 196, 26, 0.6);
    color: #73d13d;
    .status-dot {
      background: #73d13d;
      box-shadow: 0 0 6px #73d13d;
    }
  }

  &.status-3 {
    background: rgba(245, 34, 45, 0.2);
    border: 1px solid rgba(245, 34, 45, 0.6);
    color: #ff4d4f;
    .status-dot { background: #ff4d4f; }
  }
}

/* 操作列按钮 (单行整齐排列) */
.table-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  gap: 8px;

  .btn-dl,
  .btn-dl-disabled {
    font-size: 12px;
    height: 24px;
    padding: 0 10px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    flex-shrink: 0;
    box-sizing: border-box;
  }

  .btn-dl {
    background: #1890ff;
    border: none;
    box-shadow: 0 2px 6px rgba(24, 144, 255, 0.4);
    color: #ffffff;
    font-weight: 600;
    &:hover {
      background: #40a9ff;
      box-shadow: 0 3px 10px rgba(0, 229, 255, 0.6);
    }
  }

  .btn-dl-disabled {
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.3);
    cursor: not-allowed;
  }

  .btn-stop,
  .btn-cancel,
  .btn-del,
  .btn-del-disabled {
    font-size: 13px;
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    flex-shrink: 0;
    line-height: 1;
    cursor: pointer;
    transition: all 0.2s;
    .anticon {
      margin-right: 2px;
      font-size: 13px;
    }
  }

  .btn-stop {
    color: #ff4d4f !important;
    font-weight: 600;
    &:hover {
      color: #ff7875 !important;
      text-shadow: 0 0 8px rgba(255, 77, 79, 0.8);
    }
  }

  .btn-cancel {
    color: #fa8c16 !important;
    font-weight: 500;
    &:hover {
      color: #ffa940 !important;
      text-shadow: 0 0 6px rgba(250, 140, 22, 0.7);
    }
  }

  .btn-del {
    color: #ff4d4f !important;
    font-weight: 500;
    &:hover {
      color: #ff7875 !important;
      text-shadow: 0 0 6px rgba(255, 77, 79, 0.6);
    }
  }

  .btn-del-disabled {
    color: rgba(255, 255, 255, 0.35);
    cursor: not-allowed;
  }
}

/* 分页组件 */
/deep/ .ant-pagination {
  margin-top: 14px;
  color: rgba(255, 255, 255, 0.8) !important;

  .ant-pagination-total-text {
    color: rgba(255, 255, 255, 0.55);
  }

  .ant-pagination-item {
    background: rgba(10, 36, 80, 0.8) !important;
    border: 1px solid rgba(24, 144, 255, 0.35) !important;
    border-radius: 4px;
    a {
      color: rgba(255, 255, 255, 0.85) !important;
    }
    &:hover {
      border-color: #00e5ff !important;
      a { color: #00e5ff !important; }
    }
  }

  .ant-pagination-item-active {
    background: #1890ff !important;
    border-color: #1890ff !important;
    box-shadow: 0 0 8px rgba(24, 144, 255, 0.6);
    a {
      color: #ffffff !important;
      font-weight: 700;
    }
  }

  .ant-pagination-prev .ant-pagination-item-link,
  .ant-pagination-next .ant-pagination-item-link {
    background: rgba(10, 36, 80, 0.8) !important;
    border: 1px solid rgba(24, 144, 255, 0.35) !important;
    color: rgba(255, 255, 255, 0.8) !important;
    border-radius: 4px;
  }

  .ant-select-selection {
    background: rgba(10, 36, 80, 0.85) !important;
    border: 1px solid rgba(24, 144, 255, 0.4) !important;
    color: #ffffff !important;
  }

  .ant-pagination-options-quick-jumper input {
    background: rgba(10, 36, 80, 0.85) !important;
    border: 1px solid rgba(24, 144, 255, 0.4) !important;
    color: #ffffff !important;
  }
}

.empty-holder {
  padding: 24px 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.45);
  .empty-icon {
    font-size: 32px;
    color: rgba(24, 144, 255, 0.35);
    margin-bottom: 8px;
  }
}
</style>

<style lang="less">
/* 全局覆盖弹窗宽度与容器背景(解决modal挂在body下scoped不生效问题) */
.tech-export-modal-wrap {
  .ant-modal {
    width: 980px !important;
    max-width: 95vw !important;
  }
  .ant-modal-content {
    background: #0c2858 !important;
    border: 1px solid rgba(0, 229, 255, 0.4) !important;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.7) !important;
    border-radius: 8px !important;
  }
  .ant-modal-header {
    background: #0c2858 !important;
    border-bottom: 1px solid rgba(24, 144, 255, 0.3) !important;
  }
  .ant-modal-body {
    padding: 16px 20px !important;
    background: #0c2858 !important;
  }
}
</style>
